import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import RoleBadge from './roleUtils';
import ProjectsPageShell from './hub/ProjectsPageShell';
import ProjectBrowseKindIcon from './hub/ProjectBrowseKindIcon';
import { IconSettings } from './hub/HubRowActionIcons';
import HubRowPrimaryTrailIcon from './hub/HubRowPrimaryTrailIcon';
import HubProjectStatusChip from './hub/HubProjectStatusChip';
import { useProjectChromeBranding } from '../../providers/ProjectChromeBrandingContext';
import styles from './ProjectsHome.module.css';

/**
 * Organization landing at /projects/org/:orgName — capability-based list of
 * direct child orgs and projects the current user can use.
 */
function OrganizationHome() {
    const { orgName } = useParams();
    const navigate = useNavigate();
    const { setResolvedBranding } = useProjectChromeBranding();

    const [folder, setFolder] = useState(null);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (!orgName) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const encoded = encodeURIComponent(orgName);
        api.get(`projects/org/by-name/${encoded}`, {})
            .then((r) => {
                if (cancelled) return;
                if (r.status === 200) {
                    setFolder(r.data?.folder ?? null);
                    setChildren(r.data?.folders ?? []);
                    if (r.data?.resolvedBranding) {
                        setResolvedBranding(r.data.resolvedBranding);
                    } else if (r.data?.folder) {
                        setResolvedBranding({
                            title: '',
                            logoItemId: null,
                            organizationFolderId: String(r.data.folder._id),
                            organizationName: r.data.folder.name || orgName,
                        });
                    }
                } else if (r.status === 403) {
                    setError(r.data?.message || 'You do not have access to this organization.');
                    setFolder(null);
                    setChildren([]);
                } else if (r.status === 404) {
                    setError(r.data?.message || 'Organization not found.');
                    setFolder(null);
                    setChildren([]);
                } else {
                    setError(r.data?.message || 'Failed to load organization.');
                    setFolder(null);
                    setChildren([]);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e.message || 'Failed to load organization.');
                    setFolder(null);
                    setChildren([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [orgName, retryCount, setResolvedBranding]);

    const orgFolders = children.filter((f) => f.girderProjects?.type === 'organization');
    const projects = children.filter((f) => f.girderProjects?.type === 'project');
    const brandingTitle = (folder?.girderProjects?.branding?.title || '').trim();
    const pageTitle = folder?.name || decodeURIComponent(orgName || '');
    const subtitle = brandingTitle && brandingTitle !== pageTitle
        ? brandingTitle
        : 'Projects and folders in this working group.';

    return (
        <ProjectsPageShell
            title={pageTitle}
            subtitle={!loading && !error ? subtitle : undefined}
            loading={loading}
            error={error}
            onRetry={error ? () => setRetryCount((c) => c + 1) : undefined}
        >
            {!loading && !error && orgFolders.length === 0 && projects.length === 0 ? (
                <p className={styles.emptyStatePlain}>
                    No projects or folders are available to you here yet.
                </p>
            ) : null}

            {!loading && !error && orgFolders.length > 0 ? (
                <section className={styles.browseSection} aria-labelledby="org-home-folders-h" style={{ marginBottom: '1.5em' }}>
                    <h2 id="org-home-folders-h" className={styles.browseSectionHeading}>Folders</h2>
                    <ul className={styles.hubList}>
                        {orgFolders.map((org) => (
                            <li key={org._id} className={`${styles.hubRow} ${styles.hubRowFolder}`}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`${styles.hubRowPrimary} ${styles.hubRowPrimaryFolder}`}
                                    onClick={() => navigate(`/projects/org/${encodeURIComponent(org.name)}`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/projects/org/${encodeURIComponent(org.name)}`);
                                        }
                                    }}
                                    aria-label={`Open folder: ${org.name}`}
                                    title={`Open folder — ${org.name}`}
                                >
                                    <div className={styles.hubRowMain}>
                                        <div className={styles.hubRowTitleRow}>
                                            <span className={`${styles.browseKind} ${styles.browseKindFolder}`} title="Folder">
                                                <ProjectBrowseKindIcon type="organization" />
                                                <span className={styles.visuallyHidden}>Folder</span>
                                            </span>
                                            <span className={styles.hubRowName}>{org.name}</span>
                                        </div>
                                    </div>
                                    <span className={styles.hubRowPrimaryTrail}>
                                        <HubRowPrimaryTrailIcon variant="folder" />
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {!loading && !error && projects.length > 0 ? (
                <section className={styles.browseSection} aria-labelledby="org-home-projects-h">
                    <h2 id="org-home-projects-h" className={styles.browseSectionHeading}>Projects</h2>
                    <ul className={styles.hubList}>
                        {projects.map((proj) => {
                            const roles = proj.myRoles || [];
                            const isAdmin = roles.includes('admin');
                            return (
                                <li key={proj._id} className={`${styles.hubRow} ${styles.hubRowProject}`}>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className={`${styles.hubRowPrimary} ${styles.hubRowPrimaryProject}`}
                                        onClick={() => navigate(`/projects/${proj._id}/participate`)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                navigate(`/projects/${proj._id}/participate`);
                                            }
                                        }}
                                        aria-label={`Open project workspace: ${proj.name}`}
                                        title={`Open project workspace — ${proj.name}`}
                                    >
                                        <div className={styles.hubRowMain}>
                                            <div className={styles.hubRowTitleRow}>
                                                <span className={`${styles.browseKind} ${styles.browseKindProject}`} title="Project">
                                                    <ProjectBrowseKindIcon type="project" />
                                                    <span className={styles.visuallyHidden}>Project</span>
                                                </span>
                                                <span className={styles.hubRowName}>{proj.name}</span>
                                                <HubProjectStatusChip lifecycleStatus={proj.girderProjects?.lifecycleStatus} />
                                            </div>
                                            {roles.length > 0 ? (
                                                <div className={styles.roleChipList} aria-label="Your roles">
                                                    {roles.map((role) => (
                                                        <span key={role} className={styles.roleChip}>
                                                            <RoleBadge role={role} variant="long" />
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        <span className={styles.hubRowPrimaryTrail}>
                                            <HubRowPrimaryTrailIcon variant="project" />
                                        </span>
                                    </div>
                                    <div className={styles.hubRowActions}>
                                        {isAdmin ? (
                                            <button
                                                type="button"
                                                className={styles.hubIconBtn}
                                                title="Project settings"
                                                aria-label="Project settings"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/projects/${proj._id}`);
                                                }}
                                            >
                                                <IconSettings />
                                            </button>
                                        ) : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}
        </ProjectsPageShell>
    );
}

export default OrganizationHome;
